// Navigation: Autopilot und Lichtflug.
//
// Beides beantwortet dieselbe Frage — „die Welt ist endlos, wie komme ich
// dorthin?" — und beides ist **reine Rechnung**: hier steht keine Eingabe, kein
// Phaser und kein Zustand ausser dem, den der Aufrufer haelt. Genau deshalb
// laesst sich beides in Node testen (Gate FG-18).
//
// **Der Autopilot ist keine zweite Steuerung.** Er erzeugt dieselbe
// `ShipInput`, die Tastatur und Maus erzeugen — Beschleunigungsrichtung und
// Boost, mehr nicht. Damit gilt für ihn jede Regel des Schiffs unveraendert,
// und der Server merkt von ihm nichts: der Client meldet weiter nur seinen Ort
// (Gate FG-32).
//
// **Der Lichtflug ist dagegen sehr wohl eine Sache des Servers** — nicht als
// Nachricht, sondern als Schiffswert. `warpMul` steht in den Grundwerten und
// damit im Spielstand; die Bewegungspruefung liest ihn dort (`pve/mining.py`,
// `move_reach`). Ein Client, der „ich springe gerade" melden müsste, wäre die
// vierte Client-Nachricht — und die gibt es nicht.

import { build, nodes, worldLimit } from '../data/balancing';
import type { DerivedStats } from '../data/types';
import { distStr } from '../core/format';
import { clamp } from '../core/math';

/** Die drei Zustände des Sprungfelds. */
export type WarpMode = 'off' | 'spooling' | 'on';

export interface WarpState {
  mode: WarpMode;
  /** Sekunden im aktuellen Zustand — der Anlauf misst sich daran. */
  elapsed: number;
}

export const newWarpState = (): WarpState => ({ mode: 'off', elapsed: 0 });

/**
 * Was der Lichtflug in diesem Schritt bedeutet.
 *
 * `mul` ist der Geschwindigkeitsfaktor (1, solange das Feld nicht steht),
 * `drain` die Energie je Sekunde.
 */
export interface WarpStep {
  mul: number;
  drain: number;
}

/** Ein neutraler Schritt — geteilt, kein neues Objekt je Frame (Gate FG-14). */
const KEIN_WARP: WarpStep = { mul: 1, drain: 0 };

/**
 * Darf das Sprungfeld überhaupt stehen?
 *
 * Vier Riegel, und jeder hat einen Grund:
 *
 * * **Freigeschaltet** — sonst wäre der Lichtflug ein Knopf ohne Kauf.
 * * **Energie** — er ist Reibung wie alles andere: leer heißt „er endet",
 *   nicht „das Schiff steht" (Gate FG-29).
 * * **Kein Gefecht** — wer beschossen wird, springt nicht davon; sonst wäre
 *   der Wachbereich eines Gegners keine Entscheidung mehr.
 * * **Nicht abgeschleppt** — während der Notabschaltung steuert niemand.
 */
export function warpAllowed(
  stats: DerivedStats, energy: number, inCombat: boolean, frozen: boolean,
): boolean {
  return Boolean(stats.unlocks.warp) && energy > 1 && !inCombat && !frozen;
}

/**
 * Den Zustand des Sprungfelds fortschreiben und sagen, was er kostet.
 *
 * **Der Anlauf ist der ganze Unterschied zu einem Teleport.** Ohne ihn wäre
 * der Lichtflug ein Knopf, der das Schiff versetzt; mit ihm ist er eine
 * Entscheidung, die ein paar Sekunden vorher fällt — und genau so lang bleibt
 * man verwundbar.
 */
export function warpStep(
  state: WarpState, dt: number, want: boolean, stats: DerivedStats,
): WarpStep {
  const v = stats.values;
  if (!want) {
    if (state.mode !== 'off') { state.mode = 'off'; state.elapsed = 0; }
    return KEIN_WARP;
  }

  state.elapsed += dt;
  if (state.mode === 'off') { state.mode = 'spooling'; state.elapsed = dt; }
  if (state.mode === 'spooling') {
    // Der Anlauf kostet schon Energie: er ist Teil des Sprungs, nicht davor.
    if (state.elapsed < Math.max(0, v.warpSpool)) return { mul: 1, drain: v.warpDrain * 0.5 };
    state.mode = 'on';
    state.elapsed = 0;
  }
  return { mul: Math.max(1, v.warpMul), drain: v.warpDrain };
}

/** Wie weit der Anlauf ist (0…1) — reine Anzeige. */
export function warpProgress(state: WarpState, stats: DerivedStats): number {
  if (state.mode === 'on') return 1;
  if (state.mode === 'off') return 0;
  const spool = Math.max(0.01, stats.values.warpSpool);
  return clamp(state.elapsed / spool, 0, 1);
}

// ── Autopilot ────────────────────────────────────────────────────────────────

/** Ein gesetzter Kurs. Er gehört zur Oberfläche, nicht in den Spielstand. */
export interface Course {
  x: number;
  y: number;
  /** Woher er kommt — für die Beschriftung im HUD. */
  label: string;
}

/**
 * Wie nah ans Ziel, bevor der Autopilot abschaltet.
 *
 * Großzügiger als die Reichweite eines Auftragsschritts: der Kurs soll enden,
 * **bevor** das Schiff das Ziel überfliegt, sonst pendelt es darum herum.
 */
export const COURSE_ARRIVE = 90;

/**
 * Ab welcher Restentfernung der Autopilot bremst statt zu beschleunigen.
 *
 * Die Zahl ist keine Balancing-Größe, sondern Teil der Kurve: sie folgt aus
 * `v²/(2a)` mit den Grundwerten und ist bewusst großzügig — ein Anflug, der zu
 * spät bremst, überschießt, und das sieht aus wie ein Fehler.
 */
const BRAKE_FACTOR = 0.62;

export interface Steering {
  ax: number;
  ay: number;
  boost: boolean;
  /** Ist der Kurs erreicht? Dann schaltet der Aufrufer ihn ab. */
  arrived: boolean;
}

/**
 * Die Steuereingabe zu einem Kurs — **in einen übergebenen Puffer** (FG-14).
 *
 * Sie tut genau das, was ein Spieler mit gehaltener Maustaste täte: in
 * Richtung Ziel beschleunigen, vor dem Ziel gegenhalten, bei langer Strecke
 * boosten. Nichts davon ist eine neue Fähigkeit — der Autopilot spart die
 * Hand, nicht die Regeln.
 */
export function steerTo(
  shipX: number, shipY: number, vx: number, vy: number,
  course: Course, stats: DerivedStats, out: Steering,
): Steering {
  const dx = course.x - shipX;
  const dy = course.y - shipY;
  const dist = Math.hypot(dx, dy);
  out.arrived = dist <= COURSE_ARRIVE;
  if (out.arrived || dist < 1e-6) {
    // Angekommen heißt **gegenhalten**, nicht loslassen: der Drag allein
    // trägt das Schiff sonst noch hunderte Einheiten weiter.
    const speed = Math.hypot(vx, vy);
    out.ax = speed > 1 ? -vx / speed : 0;
    out.ay = speed > 1 ? -vy / speed : 0;
    out.boost = false;
    return out;
  }

  const v = stats.values;
  const nx = dx / dist;
  const ny = dy / dist;
  // Bremsweg: solange er kürzer ist als die Reststrecke, wird beschleunigt.
  const speed = Math.hypot(vx, vy);
  const bremsweg = (speed * speed) / (2 * Math.max(1, v.accel)) / BRAKE_FACTOR;
  if (bremsweg >= dist && speed > 1) {
    out.ax = -vx / speed;
    out.ay = -vy / speed;
    out.boost = false;
    return out;
  }

  out.ax = nx;
  out.ay = ny;
  // Boosten lohnt nur auf Strecke — und nur, wenn die Zelle es trägt.
  out.boost = dist > COURSE_ARRIVE * 6;
  return out;
}

/**
 * Nimmt der Autopilot diesen Kurs an?
 *
 * `autoRange` ist ein Schiffswert und wächst über den Navigations-Zweig. Ein
 * Kurs quer durch die Karte ist damit **eine gekaufte Fähigkeit**, keine
 * Selbstverständlichkeit — sonst wäre der ganze Ast eine Zierde.
 */
export function courseAccepted(
  shipX: number, shipY: number, x: number, y: number, stats: DerivedStats,
): boolean {
  if (!stats.unlocks.autopilot) return false;
  return Math.hypot(x - shipX, y - shipY) <= stats.values.autoRange;
}

/**
 * Warum der Autopilot gesperrt ist — leer heißt: er ist frei.
 *
 * Der Grund kam bisher aus einem **zweiten** Lesen von `unlocks.autopilot` in
 * der Weltszene. Das ist die zweite Regelquelle, gegen die dieses Projekt an
 * drei Stellen ausdrücklich schreibt (`missingReasons`, `buildLocked`,
 * `nodeState`) — und der Kurs war die letzte Sperre ohne Gegenstück.
 *
 * Er **löst dabei die Namensgleichheit auf**: „Navigationsrechner" heißt
 * zugleich die Ausbaustufe und der Baumknoten, der sie freischaltet, und die
 * beiden verlangen verschiedene nächste Schritte — Basis bauen oder Knoten
 * kaufen. Ein Text, der beides „Navigationsrechner" nennt, schickt den halben
 * Spielerkreis in den falschen Reiter.
 *
 * Welcher Knoten den Autopiloten öffnet, wird aus der Tabelle **gesucht**, nicht
 * als ID hier hingeschrieben: eine erfundene ID wäre still falsch, sobald
 * jemand den Baum umbaut.
 */
export function courseLock(stats: DerivedStats, built: ReadonlySet<string>): string {
  if (stats.unlocks.autopilot) return '';
  const tor = nodes().find((entry) => entry.unlock === 'autopilot');
  if (!tor) return 'Dafür fehlt der Autopilot.';
  if (tor.needBuild && !built.has(tor.needBuild)) {
    const stufe = build(tor.needBuild);
    return `Dafür fehlt der Ausbau „${stufe ? stufe.name : tor.needBuild}" — Reiter „Basis".`;
  }
  return `Dafür fehlt „${tor.name}" — Reiter „Upgrades".`;
}

/**
 * Der vollständige Grund, warum dieser Kurs nicht zustande kommt.
 *
 * Er fragt zuerst `courseAccepted` und kann damit über das **Ob** nicht
 * abweichen — nur das **Warum** steht hier, und es ist erschöpfend: gesperrt
 * oder zu weit, etwas Drittes gibt es nicht.
 */
export function courseReason(
  shipX: number, shipY: number, x: number, y: number,
  stats: DerivedStats, built: ReadonlySet<string>,
): string {
  if (courseAccepted(shipX, shipY, x, y, stats)) return '';
  const gesperrt = courseLock(stats, built);
  if (gesperrt) return gesperrt;
  return 'Zu weit für den Navigationsrechner — flieg näher heran. '
    + `Reichweite: ${distStr(stats.values.autoRange)}.`;
}

/** Der Halbmesser, den die Karte zeigt — `mapRange`, geklemmt auf die Alphawand. */
export function mapRadius(stats: DerivedStats): number {
  return Math.min(worldLimit(), Math.max(1200, stats.values.mapRange));
}

/**
 * Die Suchweite der Peilung — `bearingRange`, geklemmt auf die Alphawand.
 *
 * Dieselbe Klemme wie bei der Karte, und aus demselben Grund: eine Front
 * jenseits der Wand ist keine Antwort, sondern ein Pfeil auf einen Ort, an den
 * niemand fliegen kann. Sie liest die Grenze über `worldLimit()`, damit die
 * Peilung nicht ihre eigene Fassung davon führt.
 *
 * Sie ist zugleich die **Kostenschranke** des Scans: ohne sie wüchse die
 * durchsuchte Fläche mit einem Schiffswert, den kein Weltparameter begrenzt.
 */
export function bearingRadius(stats: DerivedStats): number {
  return Math.min(worldLimit(), Math.max(0, stats.values.bearingRange));
}
